import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, inject, input, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Subscription, filter, first, take } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { BaseStoreService, CategoryService, LoaderService, LocationService, PrudsysService, UrlMappingService } from '@seb/web-common/core/services';
import { BrandsEnum, IBrandLogo } from '@seb/web-design-system/core';
import { CustomCategory } from '@seb/web-common/core/models';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class AbstractCategoryPageComponent implements OnInit, OnDestroy {
  class = input('');

  protected urlMappingService = inject(UrlMappingService);
  protected baseStoreService = inject(BaseStoreService);
  protected categoryService = inject(CategoryService);
  protected locationService = inject(LocationService);
  protected prudsysService = inject(PrudsysService);
  protected loaderService = inject(LoaderService);
  protected cdRef = inject(ChangeDetectorRef);
  protected platformId = inject(PLATFORM_ID);

  brandLogo: IBrandLogo;
  brandCorner: BrandsEnum;
  category: CustomCategory;
  subscriptions = new Subscription();

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class(), 'wc-template'].filter(Boolean).join(' ');
  }

  ngOnInit(): void {
    this.loaderService.showGlobalLoader();

    this.subscriptions.add(
      this.categoryService.getCurrentCategory().pipe(
        filter((cat) => !!cat)
      ).subscribe((category) => {
        const brandFound = this.baseStoreService.getBrand(category['brandCorner']);
        const newBrand = brandFound?.id as BrandsEnum;

        this.brandLogo = brandFound?.logo ? { media: brandFound.logo } : undefined;
        this.category = category;

        if (newBrand !== this.brandCorner) {
          this.brandCorner = newBrand;
          this.onBrandCornerChanged();
        }

        this.onCategoryChanged();

        if (isPlatformBrowser(this.platformId)) {
          if (category.url?.startsWith('/accessory')) {
            this.subscriptions.add(
              this.urlMappingService.getUrlMappingByCode('/accessory').pipe(take(1)).subscribe((urlMapping) => {
                const url = category.url.replace(urlMapping.code, urlMapping.value);
                this.locationService.replaceUrl(url);
              })
            );
          } else {
            this.locationService.replaceUrl(category.url);
          }

          if (this.prudsysService.isEnabled()) {
            this.subscriptions.add(
              this.prudsysService.sendCategoryViewEvent(category.code).pipe(first()).subscribe()
            );
          }
        }

        this.loaderService.hideGlobalLoader();
        this.cdRef.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected onCategoryChanged(): void {
    /* Empty */
  }

  protected onBrandCornerChanged(): void {
    /* Empty */
  }
}
